/**
 * @author Ketan Shetye
 * @created 2025-10-09
 * @description applicant home routing
 */

import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Routes
} from '@angular/router';
import {
  ApplicantJanusGuard,
  AuthGuard,
  CompletedApplicationGuard
} from 'src/app/services/auth.guard';
import { applicantHomeRoutesNames } from 'src/app/services/mock-data/applicant-home-constants/applicant-home-constants';
import { PermitFormComponent } from 'src/app/views/applicant/apply-for-permit/permit-form/permit-form.component';
import {
  ApplicantHomeComponent,
  ApplyForPermitComponent,
  ChangePasswordComponent,
  ContactComponent,
  EditProfileComponent,
  FaqComponent,
  MyApplicationsComponent,
  MyRegistrationsComponent
} from 'src/app/views/export.views';
import { AppViewComponent } from 'src/app/views/master-views/app.view/app.view.component';
import { ApplicationSummaryComponent } from 'src/app/views/workflow/application-summary/application-summary.component';
import { MyContractorRegistrationsComponent } from 'src/app/views/applicant/my-contractor-registration/my-contractor-registrations.component';
import { ContractorRegistrationSelectorComponent } from 'src/app/views/applicant/my-contractor-registration/contractor-registration-selector/contractor-registration-selector.component';
import { AppViewShellNavigationGuard } from 'src/app/services/app-view-shell-navigation.guard';
import { MyContractorRegistrationDetailsComponent } from 'src/app/views/applicant/my-contractor-registration/my-contractor-registration-details/my-contractor-registration-details.component';

export const ApplicantHomeRoutes: Routes = [
  {
    path: 'jurisdiction/:clientId',
    // canActivate:[ApplicantJanusGuard],
    children: [
      {
        path: applicantHomeRoutesNames.root,
        component: ApplicantHomeComponent,
        // Added by Amisha on 2026-06-12.
        // Reason: catch applicant-side shell navigation in one place whenever Janus AppView is open.
        canActivateChild: [AppViewShellNavigationGuard],
        children: [
          {
            path: '',
            redirectTo: applicantHomeRoutesNames.applyForPermit.route,
            pathMatch: 'full'
          },

          {
            path: applicantHomeRoutesNames.applyForPermit.route,
            component: ApplyForPermitComponent,
            children: [
              {
                path: `department/:departmentId`,
                component: ApplyForPermitComponent
              }
            ]
          },

          {
            path: `${applicantHomeRoutesNames.applyForPermit.route}/department/:departmentId/application/:applicationId`,
            component: AppViewComponent,
            canActivate: [
              (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
                inject(CompletedApplicationGuard).canActivate(next, state)
            ]
          },
          {
            path: `${applicantHomeRoutesNames.applyForPermit.route}/department/:departmentId/child-application/:applicationId`,
            component: AppViewComponent,
            data: {
              type: 'child-application'
            }
          },
          {
            path: applicantHomeRoutesNames.myApplications.route,
            component: MyApplicationsComponent,
            canActivate: [
              (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
                inject(AuthGuard).canActivate(next, state)
            ],

            children: [
              {
                path: 'workflow-application/:applicationId',
                component: AppViewComponent,
                canActivate: [
                  (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
                    inject(CompletedApplicationGuard).canActivate(next, state)
                ],
                data: { showHamburger: true }
              },
              {
                path: 'child-application/:applicationId',
                component: AppViewComponent,
                data: {
                  type: 'child-application'
                }
              },
              {
                path: 'workflow-application/:applicationId/:activityDataId',
                component: AppViewComponent,
                canActivate: [
                  (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
                    inject(CompletedApplicationGuard).canActivate(next, state)
                ],
                data: { showHamburger: true }
              },
              {
                path: 'workflow-application-print-summary/:applicationId',
                component: ApplicationSummaryComponent,
                data: { printMode: true, isJanus: true }
              },
              {
                path:
                  'application/workflow-application-print-summary/:applicationId',
                component: ApplicationSummaryComponent,
                data: { printMode: true, isJanus: true }
              }
            ]
          },
          // Added by amisha on 05/21/2026 for new renewal component
          {
            path: applicantHomeRoutesNames.myRenewals.route,
            component: MyRegistrationsComponent,
            canActivate: [
              (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
                inject(AuthGuard).canActivate(next, state)
            ],
            children: [
              {
                path: 'workflow-application/:applicationId/:activityDataId',
                component: AppViewComponent,
                canActivate: [
                  (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
                    inject(CompletedApplicationGuard).canActivate(next, state)
                ],
                data: { showHamburger: true }
              }
            ]
          },

          //Start -  by Juber
          {
            path: applicantHomeRoutesNames.myContractorRegistrations.route,
            component: MyContractorRegistrationsComponent,
            canActivate: [
              (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
                inject(AuthGuard).canActivate(next, state)
            ],
            children: [
              {
                path: 'new-registration',
                component: ContractorRegistrationSelectorComponent
              },
              {
                path: 'details/:registrationId',
                component: MyContractorRegistrationDetailsComponent
              },
              {
                path: 'workflow-application/:applicationId/:activityDataId',
                component: AppViewComponent,
                canActivate: [
                  (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
                    inject(CompletedApplicationGuard).canActivate(next, state)
                ],
                data: { showHamburger: true }
              },
              {
                path: 'workflow-application/:applicationId',
                component: AppViewComponent,
                canActivate: [
                  (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
                    inject(CompletedApplicationGuard).canActivate(next, state)
                ],
                data: { showHamburger: true }

              },
              {
                path: 'workflow-application-print-summary/:applicationId',
                component: ApplicationSummaryComponent,
                data: { printMode: true, isJanus: true }
              },
            ]
          },
          //End -  by Juber

          {
            path: applicantHomeRoutesNames.contact.route,
            component: ContactComponent
          },
          {
            path: applicantHomeRoutesNames.faq.route,
            component: FaqComponent
          },
          //Code added by Amisha on 2025-11-12
          {
            path: applicantHomeRoutesNames.editProfile.route,
            component: EditProfileComponent,
            data: { hideSidebar: true }
          },
          {
            path: applicantHomeRoutesNames.changePassword.route,
            component: ChangePasswordComponent,
            data: { hideSidebar: true }
          }
          //Code end by Amisha on 2025-11-12
        ]
      }
    ]
  }
];
